import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './AccountHub.css';
import {
  FaChevronRight,
  FaUserCircle,
  FaLock,
  FaBell,
  FaEnvelope,
  FaFileAlt,
  FaWrench,
  FaClipboardList,
  FaMoneyBillWave,
  FaReceipt,
  FaShieldAlt,
  FaHistory,
  FaStar,
  FaUserTimes,
} from 'react-icons/fa';
import {
  MdVerified,
  MdOutlineReceiptLong,
  MdPayments,
  MdVerifiedUser,
  MdOutlineHome,
  MdHomeWork,
  MdBuild,
  MdExitToApp,
  MdAttachMoney,
  MdEventAvailable,
} from 'react-icons/md';
import { getProfile } from '../../../api/services/authService';
import { getRentalStatus, submitMoveOutRequest } from '../../../api/services/rentalService';
import { getTenantScoreAnalytics } from '../../../api/services/analyticsService';
import PayRentEvidenceModal from './PayRentEvidenceModal';
import TenantKYCModal from './TenantKYCModal';
import ComingSoonModal from './ComingSoonModal';

const formatMoneyKes = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';

  const hasCents = Math.floor(num) !== num;
  const formatted = num.toLocaleString(undefined, {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  });

  return `KES ${formatted}`;
};

const formatDueDate = (dueDate) => {
  if (!dueDate || Number.isNaN(dueDate.getTime())) return '—';
  return dueDate.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
};

const getNextDue = (rentDueDay) => {
  const dueDay = Number(rentDueDay);
  if (!Number.isFinite(dueDay) || dueDay < 1 || dueDay > 28) return null;

  const now = new Date();
  const candidate = new Date(now.getFullYear(), now.getMonth(), dueDay, 12, 0, 0, 0);
  if (candidate < now) {
    candidate.setMonth(candidate.getMonth() + 1);
  }

  const diffMs = candidate.getTime() - now.getTime();
  const days = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));

  return { dueDate: candidate, days };
};

const displayTenantScore = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return (Math.round(n * 10) / 10).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
};

const getTenantScoreBand = (score) => {
  const n = Number(score);
  if (!Number.isFinite(n)) {
    return { label: 'No score yet', className: 'score-badge--neutral' };
  }
  if (n >= 80) return { label: 'Excellent', className: 'score-badge--excellent' };
  if (n >= 65) return { label: 'Good', className: 'score-badge--good' };
  if (n >= 50) return { label: 'Fair', className: 'score-badge--fair' };
  if (n >= 35) return { label: 'Needs work', className: 'score-badge--low' };
  return { label: 'No score yet', className: 'score-badge--risk' };
};

const resolveKycStatusFromProfile = (profile) => {
  const topLevelStatus = String(profile?.kycStatus || '').toUpperCase();
  const nestedStatus = String(profile?.kyc?.status || '').toUpperCase();

  if (topLevelStatus) return topLevelStatus;
  if (nestedStatus) return nestedStatus;

  const hasKycObject = Boolean(profile?.kyc?.id);
  const hasKycDocs = Boolean(profile?.kyc?.idDocumentUrl || profile?.kyc?.idDocumentBackUrl || profile?.kyc?.selfieUrl);
  if (hasKycObject || hasKycDocs) return 'PENDING';

  return null;
};

const AccountHub = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [rental, setRental] = useState(null);
  const [rentalLoading, setRentalLoading] = useState(true);
  const [rentalError, setRentalError] = useState('');
  const [rentalRefetchKey, setRentalRefetchKey] = useState(0);
  const [tenantScoreData, setTenantScoreData] = useState(null);
  const [tenantScoreLoading, setTenantScoreLoading] = useState(true);
  const [payRentModalOpen, setPayRentModalOpen] = useState(false);
  const [kycModalOpen, setKycModalOpen] = useState(false);
  const [comingSoonModalOpen, setComingSoonModalOpen] = useState(false);
  const [moveOutModalOpen, setMoveOutModalOpen] = useState(false);
  const [moveOutDate, setMoveOutDate] = useState('');
  const [moveOutReason, setMoveOutReason] = useState('No reason');
  const [moveOutSubmitting, setMoveOutSubmitting] = useState(false);
  const [moveOutError, setMoveOutError] = useState('');
  const [moveOutSuccess, setMoveOutSuccess] = useState('');
  const [moveOutPortalRoot, setMoveOutPortalRoot] = useState(null);

  const handleDeleteAccountNavigation = useCallback(() => {
    navigate('/tenant/deleteaccount');
  }, [navigate]);

  useEffect(() => {
    if (!moveOutModalOpen) {
      setMoveOutPortalRoot(null);
      return undefined;
    }
    if (typeof document === 'undefined' || !document.body) {
      return undefined;
    }

    const el = document.createElement('div');
    el.setAttribute('data-account-moveout-modal-portal', '');
    document.body.appendChild(el);
    setMoveOutPortalRoot(el);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prevOverflow;
      if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
      setMoveOutPortalRoot(null);
    };
  }, [moveOutModalOpen]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [profileRes, rentalRes] = await Promise.allSettled([getProfile(), getRentalStatus()]);
        if (cancelled) return;

        if (profileRes.status === 'fulfilled') {
          const data = profileRes.value?.data || profileRes.value;
          setProfile(data || null);
        }

        if (rentalRes.status === 'fulfilled') {
          const rentalData = rentalRes.value?.data || null;
          setRental(rentalData);
          setRentalError('');
        } else {
          setRental(null);
          setRentalError(rentalRes.reason?.message || 'Could not load rent status.');
        }
      } finally {
        if (!cancelled) setRentalLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [rentalRefetchKey]);

  useEffect(() => {
    let cancelled = false;

    const fetchTenantScore = async () => {
      try {
        setTenantScoreLoading(true);
        const res = await getTenantScoreAnalytics(12);
        const payload = res?.data && typeof res.data === 'object' ? res.data : null;
        if (!cancelled) {
          setTenantScoreData(payload);
        }
      } catch (e) {
        if (!cancelled) {
          setTenantScoreData(null);
        }
      } finally {
        if (!cancelled) {
          setTenantScoreLoading(false);
        }
      }
    };

    fetchTenantScore();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasActiveRental = Boolean(rental && !rentalLoading);
  const unit = rental?.unit || null;
  const propertyId = rental?.propertyId || null;
  const unitId = rental?.unitId || unit?.id || null;
  const tenantCode = profile?.tenantCode ?? profile?.tenant_code ?? null;
  const monthlyRent = rental?.monthlyRent ?? unit?.monthlyRent;
  const rentDueDay = unit?.rentDueDay;
  const nextDue = getNextDue(rentDueDay);
  const unitDisplayName = rental?.unitName || unit?.name || unit?.unitNumber || null;
  const unitDisplayNumber = rental?.unitNumber || unit?.unitNumber || null;
  const rentAmount = formatMoneyKes(monthlyRent);
  const rentDueText = (() => {
    if (rentalLoading || !rental) return '';
    if (nextDue) {
      return `Due in ${nextDue.days} days\non ${formatDueDate(nextDue.dueDate)}`;
    }
    if (rentDueDay != null && Number.isFinite(Number(rentDueDay))) {
      return `Rent due on day ${rentDueDay} each month`;
    }
    return 'Rent due date not set';
  })();
  const tenantScoreTotalMonths = Number(tenantScoreData?.totalMonthsCounted);
  const tenantHasScoreWindow = Number.isFinite(tenantScoreTotalMonths) && tenantScoreTotalMonths > 0;
  const showNeutralScore = !hasActiveRental || !tenantHasScoreWindow;
  const tenantScore = showNeutralScore ? 0 : tenantScoreData?.tenantScore;
  const tenantScoreBand = showNeutralScore ? getTenantScoreBand(null) : getTenantScoreBand(tenantScore);
  const kycStatus = String(resolveKycStatusFromProfile(profile) || '').toUpperCase();
  const isKycPending = kycStatus === 'PENDING';
  const isKycVerified = kycStatus === 'APPROVED' || kycStatus === 'VERIFIED';
  const canSubmitKyc = !isKycPending && !isKycVerified;
  const noticeStatus = String(rental?.noticeStatus || '').toUpperCase();
  const alreadyOnNotice = Boolean(rental?.onNotice) || noticeStatus === 'PENDING' || noticeStatus === 'APPROVED';
  const isMoveOutActionDisabled = !hasActiveRental || alreadyOnNotice;

  const openPayRentModal = useCallback(() => {
    if (rentalLoading) return;
    if (!rental) {
      navigate('/tenant/listings');
      return;
    }
    setPayRentModalOpen(true);
  }, [rentalLoading, rental, navigate]);

  const openMoveOutModal = useCallback(() => {
    if (isMoveOutActionDisabled) return;
    const defaultDate = new Date();
    defaultDate.setMonth(defaultDate.getMonth() + 2, 0);
    setMoveOutDate(defaultDate.toISOString().substring(0, 10));
    setMoveOutReason('No reason');
    setMoveOutError('');
    setMoveOutSuccess('');
    setMoveOutModalOpen(true);
  }, [isMoveOutActionDisabled]);

  const handleSubmitMoveOut = useCallback(
    async (e) => {
      e.preventDefault();
      if (!propertyId || !unitId) {
        setMoveOutError('Active unit details are missing. Please refresh and try again.');
        return;
      }
      const date = String(moveOutDate || '').trim();
      if (!date) {
        setMoveOutError('Select a move-out date.');
        return;
      }

      try {
        setMoveOutSubmitting(true);
        setMoveOutError('');
        setMoveOutSuccess('');
        await submitMoveOutRequest(propertyId, unitId, {
          moveOutDate: date,
          reason: (moveOutReason || '').trim() || 'No reason',
        });
        setMoveOutSuccess('Move-out request submitted successfully.');
      } catch (err) {
        setMoveOutError(err?.message || 'Could not submit move-out request.');
      } finally {
        setMoveOutSubmitting(false);
      }
    },
    [propertyId, unitId, moveOutDate, moveOutReason]
  );

  const quickActions = useMemo(
    () => [
      { icon: MdEventAvailable, label: 'Booking requests', to: '/tenant/bookings' },
      { icon: MdOutlineHome, label: 'Your Home', to: '/tenant/your-home' },
      { icon: MdPayments, label: 'Payments', to: '/tenant/payment-history' },
      { icon: MdAttachMoney, label: 'Rent Loan', onClick: () => setComingSoonModalOpen(true) },
      {
        icon: MdVerifiedUser,
        label: isKycVerified ? 'KYC Verified' : isKycPending ? 'Pending' : 'Submit KYC',
        onClick: () => setKycModalOpen(true),
        disabled: !canSubmitKyc,
        tone: isKycVerified ? 'verified' : isKycPending ? 'pending' : 'danger',
      },
      { icon: MdOutlineReceiptLong, label: 'My bills', to: '/tenant/my-bills' },
      { icon: MdBuild, label: 'Maintenance', to: '/tenant/maintenance' },
      { icon: MdExitToApp, label: 'Request move out', onClick: openMoveOutModal, disabled: isMoveOutActionDisabled },
    ],
    [canSubmitKyc, isKycPending, isKycVerified, isMoveOutActionDisabled, openMoveOutModal]
  );

  const sections = useMemo(
    () => [
      {
        title: 'Payment info',
        items: [
          { label: 'Payments', icon: FaMoneyBillWave, to: '/tenant/payment-history' },
          { label: 'My bills', icon: FaReceipt, to: '/tenant/my-bills' },
        ],
      },
      {
        title: 'Manage account',
        items: [
          { label: 'Personal details', icon: FaUserCircle, to: '/tenant/profile' },
          { label: 'Security settings', icon: FaLock, to: '/tenant/profile', state: { openPasswordModal: true } },
          { label: 'Tenancy history', icon: FaHistory, to: '/tenant/tenancy-history' },
          { label: 'Delete account', icon: FaUserTimes, onClick: handleDeleteAccountNavigation, danger: true },
        ],
      },
      {
        title: 'Preferences',
        items: [
          { label: 'Notifications', icon: FaBell, to: '/tenant/notifications' },
          { label: 'Messages', icon: FaEnvelope, to: '/tenant/messages' },
        ],
      },
      {
        title: 'Trust & security',
        items: [{ label: 'My Score', icon: FaStar, to: '/tenant/score' }],
      },
      {
        title: 'Travel activity',
        items: [
          { label: 'Find a house', icon: FaShieldAlt, to: '/tenant/listings' },
          { label: 'Booking requests', icon: FaClipboardList, to: '/tenant/bookings' },
          { label: 'Your home', icon: FaShieldAlt, to: '/tenant/your-home' },
        ],
      },
      {
        title: 'Help and support',
        items: [
          { label: 'Maintenance', icon: FaWrench, to: '/tenant/maintenance' },
          { label: 'My requests', icon: FaClipboardList, to: '/tenant/requests' },
        ],
      },
      {
        title: 'Legal and privacy',
        items: [{ label: 'My reports', icon: FaFileAlt, to: '/tenant/my-reports' }],
      },
    ],
    [handleDeleteAccountNavigation]
  );

  return (
    <div className="tenant-account-hub">
      <div className="account-overview-stack">
        <div
          className={`rent-status-card${rentalLoading ? ' rent-status-card--loading' : ''}${rentalError ? ' rent-status-card--error' : ''
            }${!rentalLoading && !rental && !rentalError ? ' rent-status-card--empty' : ''}`}
        >
          <div className="rent-status-content">
            <div className="rent-status-label">RENT STATUS</div>
            {tenantCode ? <div className="rent-status-tenant-id">Tenant Code: {tenantCode}</div> : null}
            {rentalLoading ? (
              <>
                <div className="rent-status-skeleton rent-status-skeleton--amount" />
                <div className="rent-status-skeleton rent-status-skeleton--line" />
              </>
            ) : rentalError ? (
              <>
                <div className="rent-status-amount rent-status-amount--muted">Couldn&apos;t load</div>
                <div className="rent-status-due">{rentalError}</div>
                <button type="button" className="rent-status-retry" onClick={() => setRentalRefetchKey((k) => k + 1)}>
                  Retry
                </button>
              </>
            ) : !rental ? (
              <>
                <div className="rent-status-amount rent-status-amount--small">No active lease</div>
                <div className="rent-status-due">When you rent a unit, your monthly rent and due date show here.</div>
              </>
            ) : (
              <>
                <div className="rent-status-amount">{rentAmount}</div>
                <div className="rent-status-due">
                  {rentDueText.split('\n').map((line, idx) => (
                    <React.Fragment key={idx}>
                      {line}
                      {idx === 0 ? <br /> : null}
                    </React.Fragment>
                  ))}
                </div>
              </>
            )}
          </div>

          {!rentalLoading && rentalError && (
            <button type="button" className="pay-now-btn pay-now-btn--secondary" onClick={() => navigate('/tenant/listings')}>
              Listings
            </button>
          )}
          {!rentalLoading && !rentalError && hasActiveRental && (
            <button type="button" className="pay-now-btn" onClick={openPayRentModal}>
              Pay Now
            </button>
          )}
          {!rentalLoading && !rentalError && !rental && (
            <button type="button" className="pay-now-btn" onClick={() => navigate('/tenant/listings')}>
              Explore listings
            </button>
          )}
        </div>

        <div className="score-wallet-row">
          <div className="tenant-score-card">
            <div className="card-label">Tenant Score</div>
            <div className="score-display">
              {tenantScoreLoading ? (
                <span className="score-skeleton score-skeleton--value" aria-hidden />
              ) : (
                <span className="score-value">{displayTenantScore(tenantScore)}</span>
              )}
              <MdVerified className="verified-icon" />
            </div>
            {tenantScoreLoading ? (
              <div className="score-skeleton score-skeleton--badge" aria-hidden />
            ) : (
              <div className={`score-badge ${tenantScoreBand.className}`}>{tenantScoreBand.label}</div>
            )}
          </div>
          <div className="wallet-card">
            <div className="wallet-label-row">
              <div className="card-label">Active Lease</div>
              <MdHomeWork className="wallet-icon" />
            </div>
            {rentalLoading ? (
              <>
                <div className="score-skeleton score-skeleton--lease-title" aria-hidden />
                <div className="score-skeleton score-skeleton--lease-sub" aria-hidden />
              </>
            ) : rental ? (
              <>
                <div className="wallet-amount wallet-amount--compact" title={rental.propertyName || 'Property'}>
                  {rental.propertyName || 'Property'}
                </div>
                <div className="wallet-currency">
                  {(unitDisplayNumber || unitDisplayName || 'Unit')} - Active
                </div>
              </>
            ) : (
              <>
                <div className="wallet-amount wallet-amount--compact">No lease</div>
                <div className="wallet-currency">Explore listings</div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="account-quick-actions">
        <div className="account-quick-actions__title">Quick Actions</div>
        <div className="account-quick-actions__grid">
          {quickActions.map((action) => (
            <button
              key={action.label}
              type="button"
              className={`account-quick-action ${action.tone ? `account-quick-action--${action.tone}` : ''}`}
              disabled={action.disabled}
              onClick={() => {
                if (action.onClick) {
                  action.onClick();
                  return;
                }
                navigate(action.to);
              }}
            >
              <span className="account-quick-action__icon" aria-hidden="true">
                {React.createElement(action.icon)}
              </span>
              <span className="account-quick-action__label">{action.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="tenant-account-hub__grid">
        {sections.map((section) => (
          <section key={section.title} className="account-card">
            <div className="account-card__title">{section.title}</div>
            <div className="account-card__list">
              {section.items.map((row) => (
                <button
                  key={row.label}
                  type="button"
                  className={`account-row${row.danger ? ' account-row--danger' : ''}`}
                  onClick={() => {
                    if (row.onClick) {
                      row.onClick();
                      return;
                    }
                    navigate(row.to, row.state ? { state: row.state } : undefined);
                  }}
                >
                  <span className="account-row__left">
                    <span className="account-row__icon" aria-hidden="true">
                      {React.createElement(row.icon)}
                    </span>
                    <span className="account-row__label">{row.label}</span>
                  </span>
                  <span className="account-row__chev" aria-hidden="true">
                    <FaChevronRight />
                  </span>
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>

      <PayRentEvidenceModal
        open={payRentModalOpen}
        onClose={() => setPayRentModalOpen(false)}
        rental={rental}
      />

      <TenantKYCModal
        open={kycModalOpen}
        onClose={() => setKycModalOpen(false)}
        userId={profile?.id || profile?.userId}
      />

      <ComingSoonModal
        isOpen={comingSoonModalOpen}
        onClose={() => setComingSoonModalOpen(false)}
        title="Rent Loans"
        message="We are building a seamless way for you to access rent loans and short-term credit. Coming very soon!"
      />

      {moveOutModalOpen && moveOutPortalRoot
        ? createPortal(
          <div className="account-moveout-overlay" role="presentation">
            <button
              type="button"
              className="account-moveout-backdrop"
              aria-label="Close move out request"
              onClick={() => !moveOutSubmitting && setMoveOutModalOpen(false)}
            />
            <div className="account-moveout-panel" role="dialog" aria-modal="true" aria-labelledby="account-moveout-title">
              <div className="account-moveout-header">
                <h3 id="account-moveout-title">Request Move Out</h3>
              </div>

              {moveOutSuccess ? (
                <div className="account-moveout-success">
                  <p>{moveOutSuccess}</p>
                  <button type="button" className="account-moveout-submit" onClick={() => setMoveOutModalOpen(false)}>
                    Close
                  </button>
                </div>
              ) : (
                <form className="account-moveout-form" onSubmit={handleSubmitMoveOut}>
                  <div className="account-moveout-row">
                    <label htmlFor="account-moveout-date">Move-out date</label>
                    <input
                      id="account-moveout-date"
                      type="date"
                      value={moveOutDate}
                      onChange={(e) => setMoveOutDate(e.target.value)}
                      disabled={moveOutSubmitting}
                      required
                    />
                  </div>

                  <div className="account-moveout-row">
                    <label htmlFor="account-moveout-reason">Reason</label>
                    <textarea
                      id="account-moveout-reason"
                      rows={3}
                      value={moveOutReason}
                      onChange={(e) => setMoveOutReason(e.target.value)}
                      placeholder="No reason"
                      disabled={moveOutSubmitting}
                    />
                  </div>

                  {moveOutError ? <p className="account-moveout-error">{moveOutError}</p> : null}

                  <div className="account-moveout-actions">
                    <button
                      type="button"
                      className="account-moveout-cancel"
                      onClick={() => setMoveOutModalOpen(false)}
                      disabled={moveOutSubmitting}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="account-moveout-submit" disabled={moveOutSubmitting}>
                      {moveOutSubmitting ? 'Submitting...' : 'Submit request'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>,
          moveOutPortalRoot
        )
        : null}
    </div>
  );
};

export default AccountHub;

